import { Credentials } from "@fairfleet/geotab";
import GlobalStyles from "@mui/material/GlobalStyles";
import createTheme from "@mui/material/styles/createTheme";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { LicenseInfo } from "@mui/x-license";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ComponentType, ReactNode, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { MuiLocalizationProvider } from "../components/MuiLocalizationProvider";
import i18n from "../i18n";
import { createQueryClient } from "./createQueryClient";
import { mapLazy } from "./mapLazy";
import { AddinState } from "./models/AddinState";
import { removeLegacyLocalStorageKeys } from "./removeLegacyLocalStorageKeys";
import { AddInStateProvider } from "@/components/AddInStateProvider";
import { AuthBoundary } from "@/components/AuthBoundary";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { GeotabClientProvider } from "@/components/GeotabClientContext";
import { AddInNameContext } from "@/components/layout/AddInNameContext";
import { LicensedBoundary } from "@/components/LicensedBoundary";
import { useUserLanguage } from "@/hooks/useUserLanguage";
import { themeOptions } from "@/theme";

//globalThis.geotabClient = createGeotab();
LicenseInfo.setLicenseKey(
  "6a34f12212ba43ddf833e5770ac5d5b4Tz0xMjU0MTMsRT0xODAyMDQ0Nzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=",
);
removeLegacyLocalStorageKeys();
const queryClient = createQueryClient();

const MuiSnackbarProvider = mapLazy(
  "MuiSnackbarProvider",
  import("../components/MuiSnackbarProvider"),
);

const theme = createTheme(themeOptions);

// The add-in runs inside light MyGeotab: opt out of OS/browser dark mode, and paint the page
// background ourselves. A transparent body lets a dark host (embedding view, dark browser canvas)
// show through behind grids and inputs whose text stays dark.
document.documentElement.style.colorScheme = "only light";
document.body.style.backgroundColor = theme.palette.background.default;

/**
 * Renders the given add-in with the given add-in name.
 *
 * @param name - The name of the add-in as defined in `public/addin.json`
 * @param component - The component to render.
 */
export function renderAddIn(name: string, component: ComponentType) {
  if (globalThis.geotab?.addin) {
    globalThis.geotab.addin[name] = () => ({
      async initialize(api, _, callback) {
        api.getSession(_credentials => {
          /* globalThis.geotabClient = createGeotab({ credentials });
          globalThis.geotabLoginResult = credentials; */
          //render(component, state);
          callback();
        });
      },
      async focus(api, state) {
        api.getSession(credentials => {
          /* globalThis.geotabClient = createGeotab({ credentials });
          globalThis.geotabLoginResult = credentials; */
          render(name, component, state as AddinState, credentials);
        });
      },

      blur(_) {
        //
      },
    });
  } else {
    render(name, component, {});
  }
}

function render(name: string, Page: ComponentType, state: AddinState, credentials?: Credentials) {
  const rootEl = document.getElementById("fairfleet-root");
  if (!rootEl) {
    throw new Error("Could not find root element");
  }
  createRoot(rootEl).render(
    <StrictMode>
      <ErrorBoundary>
        <I18nextProvider i18n={i18n}>
          <MuiLocalizationProvider>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme}>
                <AddInStateProvider state={state}>
                  <GeotabClientProvider credentials={credentials}>
                    <UserLanguage />

                    {!credentials && (
                      <GlobalStyles
                        styles={`
                    html, body {
                      margin: 0;
                      height: 100vh;
                      overflow: auto;
                    }
                  `}
                      />
                    )}

                    {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}

                    <GlobalStyles
                      styles={
                        credentials
                          ? `
                    #fairfleet-root {
                      height: calc(100vh - 54px);
                      overflow: auto;
                    }
                  `
                          : `
                    #fairfleet-root {
                      height: 100vh;
                      overflow: auto;
                    }
                  `
                      }
                    />
                    <PageWrapper>
                      <LicensedBoundary name={name}>
                        <MuiSnackbarProvider>
                          <AddInNameContext value={name}>
                            <Suspense>
                              <Page />
                            </Suspense>
                          </AddInNameContext>
                        </MuiSnackbarProvider>
                      </LicensedBoundary>
                    </PageWrapper>
                  </GeotabClientProvider>
                </AddInStateProvider>
              </ThemeProvider>
            </QueryClientProvider>
          </MuiLocalizationProvider>
        </I18nextProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
}

/**
 * Applies the user's own language once the Geotab session says who they are.
 *
 * A component rather than a call in each page: it has to sit inside the client provider to see
 * the account, and every add-in goes through this tree. It draws nothing.
 */
function UserLanguage() {
  useUserLanguage();
  return null;
}

export function PageWrapper({ children }: { children: ReactNode }) {
  if (!globalThis.geotab) {
    return <AuthBoundary>{children}</AuthBoundary>;
  }

  return children;
}
